import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { ExpandoIcon } from '../icons/Icons';
import { FocusVisibleIndicator } from '../styles/FocusIndicators';
import { PreventUserSelect } from '../styles/PreventUserSelect';
import { TabStyles } from '../styles/TabStyles';
import { AnimationDuration } from '../utils/Animation';
import { makeTestId } from '../utils/Declarative';
import { generateId } from '../utils/DomUtil';
import { HeightAnimator } from '../utils/HeightAnimator';
import { ElemAttrs } from '../utils/HtmlAttrs';
import { InteractiveDomWrapper } from '../utils/InteractiveDom';
import { mergeEventHandlers } from '../utils/ReactHelpers';
import { Img } from './DomWidgets';

const { Spacing } = TabStyles;

export type ExpandoAlignment = 'left' | 'right';

export interface ExpandoWidgetProps {
  /**
   * Determines if the expando caret is aligned to the left or right
   * @default 'left'
   */
  alignment?: ExpandoAlignment;
  /**
   * Whether the expando expands up or down
   * Ultimately, this changes whether the control is above or below the expandable content
   * @default 'ExpandoDirection.Down'
   */
  direction?: ExpandoDirection;
  /** Set if the expando is expanded */
  isExpanded: boolean;
  /** Label for the expando button */
  label?: React.ReactChild;
  /**
   * By default children will be removed when collapsed
   * Setting this prop to `true` will keep them in the DOM with `display: none` instead
   */
  keepChildrenMounted?: boolean;
  /** Callback function to be called when the expando is closing */
  onCollapse: () => void;
  /** Callback function to be called when the expando is expanding */
  onExpand: () => void;
  /** NOTE: Applied to the root <div>, not the focusable button element. */
  style?: React.CSSProperties;
  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-Expando' will be automatically added
   */
  testId: string;
}

export enum ExpandoDirection {
  Up = 0,
  Down = 1,
}

export interface ExpandoWidgetState {
  hoveringOnExpandoButton: boolean;
}

const ExpandoButtonStyleName = TypeStyle.style({
  ...PreventUserSelect,
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
});

const ExpandoRightButtonStyleName = TypeStyle.style({
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
});

// In the collapsed state, caret either points right or left
const CollapsedIconTransform: { [key: string]: string } = Object.freeze({
  left: 'rotate(90deg)',
  right: 'rotate(-90deg)',
});

// In the expanded state, caret either points up or down
const ExpandedIconTransform: { [key: string]: string } = Object.freeze({
  [`${ExpandoDirection.Up}left`]: 'rotate(0deg)',
  [`${ExpandoDirection.Up}right`]: 'rotate(0deg)',
  [`${ExpandoDirection.Down}left`]: 'rotate(180deg)',
  [`${ExpandoDirection.Down}right`]: 'rotate(-180deg)',
});

/**
 * **NOTE**: all HTML props are applied to the focusable button element, with the exception of `style` and `className`, which are applied
 * to the root wrapper element.
 */
export class ExpandoWidget extends React.Component<ExpandoWidgetProps & ElemAttrs, ExpandoWidgetState> {
  private expandoBaseTestId: string = 'Expando';
  private contentId: string = generateId();

  public static defaultProps: Pick<ExpandoWidgetProps, 'direction' | 'alignment'> = {
    direction: ExpandoDirection.Down,
    alignment: 'left',
  };

  public state: ExpandoWidgetState = {
    hoveringOnExpandoButton: false,
  };

  private handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    this.handleExpandOrCollapse();
  }

  private handleKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      if (e.type === 'keyup') {
        this.handleExpandOrCollapse();
      }
    }
  }
  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> =
    mergeEventHandlers(this.handleKey, e => this.props.onKeyDown?.(e));
  private handleKeyUp: React.KeyboardEventHandler<HTMLDivElement> =
    mergeEventHandlers(this.handleKey, e => this.props.onKeyUp?.(e));

  private handleHover = (hover: boolean): void => {
    this.setState({ hoveringOnExpandoButton: hover });
  }

  private handleExpandOrCollapse(): void {
    if (this.props.isExpanded) {
      this.props.onCollapse();
    } else {
      this.props.onExpand();
    }
  }

  private getExpandoStyle(): React.CSSProperties {
    return {
      display: 'flex',
      flexDirection: this.props.direction === ExpandoDirection.Up ? 'column-reverse' : 'column',
      alignItems: this.props.alignment === 'right' ? 'stretch' : 'flex-start',
    };
  }

  private getExpandoIconStyle(): React.CSSProperties {
    return {
      height: ExpandoIcon.height,
      margin: 'auto',
      marginLeft: 0,
      marginRight: this.props.label ? Spacing.DefaultSpace : 0,
      transform: this.props.isExpanded ?
        ExpandedIconTransform[this.props.direction! + this.props.alignment!] :
        // collapsed state only cares about alignment, not direction
        CollapsedIconTransform[this.props.alignment!],
      transition: `transform, ${AnimationDuration}ms`,
      width: ExpandoIcon.width,
    };
  }

  public render(): JSX.Element {
    // tslint:disable-next-line: deprecation
    const { alignment, children, className, direction, isExpanded, label, keepChildrenMounted, onCollapse, onExpand, style, testId, ...htmlAttrs } = this.props;
    const contentHeight = isExpanded ? 'auto' : 0;
    return (
      <div
        className={className}
        data-tb-test-id={makeTestId(testId, this.expandoBaseTestId)}
        style={{ ...this.getExpandoStyle(), ...style }}
      >
        <InteractiveDomWrapper key='expandoInteractiveDom' handleHover={this.handleHover} pointerClick={this.handleClick}>
          <div
            {...htmlAttrs}
            {...(isExpanded && { 'aria-controls': this.contentId })}
            aria-expanded={isExpanded}
            className={TypeStyle.classes(ExpandoButtonStyleName, FocusVisibleIndicator, alignment === 'right' && ExpandoRightButtonStyleName)}
            data-tb-test-id={makeTestId(testId, this.expandoBaseTestId, 'Button')}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.handleKeyUp}
            role='button'
            tabIndex={0}
          >
            <Img key='expandoIcon'
              data-tb-test-id={makeTestId(testId, this.expandoBaseTestId, 'Icon')}
              src={ExpandoIcon.icon({ active: this.state.hoveringOnExpandoButton })}
              style={this.getExpandoIconStyle()}
            />
            {label &&
              <div key='expandoLabel' data-tb-test-id={makeTestId(testId, this.expandoBaseTestId, 'Label')}>
                {label}
              </div>
            }
          </div>
        </InteractiveDomWrapper>
        <HeightAnimator
          {...(keepChildrenMounted && { height: contentHeight })}
          initialHeight={contentHeight}
        >
          {(keepChildrenMounted || isExpanded) && <div key='expandoContent'
            data-tb-test-id={makeTestId(testId, this.expandoBaseTestId, 'Content')}
            id={this.contentId}
          >
            {children}
          </div>}
        </HeightAnimator>
      </div>
    );
  }
}
